import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  signal,
  computed,
  effect,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

export interface Step {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  completed?: boolean;
}

@Component({
  selector: "app-stepper",
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: "./stepper.component.html",
  styleUrl: "./stepper.component.scss",
})
export class StepperComponent implements AfterViewInit {
  @Input() set steps(value: Step[]) {
    this._steps.set(value);
  }

  @Input() set activeStepId(value: string | number) {
    this._activeStepId.set(value);
  }

  @Input() defaultIcon = "pending";
  @Input() linear = false;

  @Output() stepChange = new EventEmitter<Step>();

  @ViewChild("stepsContainer") stepsContainer!: ElementRef<HTMLDivElement>;

  protected _steps = signal<Step[]>([]);
  protected _activeStepId = signal<string | number>("");
  protected canScrollLeft = signal(false);
  protected canScrollRight = signal(false);

  protected activeStepIndex = computed(() =>
    this._steps().findIndex((step) => step.id === this._activeStepId()),
  );

  protected canGoToPrevious = computed(
    () =>
      this.activeStepIndex() > 0 &&
      !this._steps()[this.activeStepIndex() - 1]?.disabled,
  );

  protected canGoToNext = computed(
    () =>
      this.activeStepIndex() < this._steps().length - 1 &&
      !this._steps()[this.activeStepIndex() + 1]?.disabled,
  );

  constructor() {
    effect(() => {
      const activeId = this._activeStepId();
      if (activeId) {
        this.scrollToActiveStep();
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.checkScrollButtons(), 0);
  }

  isActive(step: Step): boolean {
    return step.id === this._activeStepId();
  }

  isCompleted(step: Step): boolean {
    if (step.completed !== undefined) {
      return step.completed;
    }

    const stepIndex = this._steps().indexOf(step);
    return stepIndex < this.activeStepIndex();
  }

  isClickable(step: Step): boolean {
    if (step.disabled) return false;
    if (!this.linear) return true;

    const stepIndex = this._steps().indexOf(step);
    return stepIndex <= this.activeStepIndex() + 1;
  }

  onStepClick(step: Step): void {
    if (!this.isClickable(step)) return;

    this._activeStepId.set(step.id);
    this.stepChange.emit(step);
  }

  goToPrevious(): void {
    if (this.canGoToPrevious()) {
      const previousStep = this._steps()[this.activeStepIndex() - 1];
      if (!previousStep.disabled) {
        this.onStepClick(previousStep);
      }
    }
  }

  goToNext(): void {
    if (this.canGoToNext()) {
      const nextStep = this._steps()[this.activeStepIndex() + 1];
      if (!nextStep.disabled) {
        this.onStepClick(nextStep);
      }
    }
  }

  scrollLeft(): void {
    if (this.stepsContainer) {
      this.stepsContainer.nativeElement.scrollBy({
        left: -200,
        behavior: "smooth",
      });
      setTimeout(() => this.checkScrollButtons(), 300);
    }
  }

  scrollRight(): void {
    if (this.stepsContainer) {
      this.stepsContainer.nativeElement.scrollBy({
        left: 200,
        behavior: "smooth",
      });
      setTimeout(() => this.checkScrollButtons(), 300);
    }
  }

  private scrollToActiveStep(): void {
    if (!this.stepsContainer) return;

    const container = this.stepsContainer.nativeElement;
    const activeElement = container.querySelector(
      ".step-item.active",
    ) as HTMLElement;

    if (activeElement) {
      const containerWidth = container.offsetWidth;
      const elementLeft = activeElement.offsetLeft;
      const elementWidth = activeElement.offsetWidth;

      const scrollPosition =
        elementLeft - containerWidth / 2 + elementWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });

      setTimeout(() => this.checkScrollButtons(), 300);
    }
  }

  checkScrollButtons(): void {
    if (this.stepsContainer) {
      const element = this.stepsContainer.nativeElement;
      this.canScrollLeft.set(element.scrollLeft > 0);
      this.canScrollRight.set(
        element.scrollLeft < element.scrollWidth - element.clientWidth,
      );
    }
  }

  onScroll(): void {
    this.checkScrollButtons();
  }

  getStepIcon(step: Step): string {
    return this.isCompleted(step)
      ? "check_circle"
      : (step.icon ?? this.defaultIcon);
  }

  get steps(): Step[] {
    return this._steps();
  }

  get activeStepId(): string | number {
    return this._activeStepId();
  }
}
